import { Divider, Segmented, Spin, Typography } from 'antd';
import IconRound from 'assets/logos/icon-round.svg?react';
import { createUseStyles } from 'react-jss';

import { DownloadOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import Title from 'antd/es/typography/Title';
import ChatExplainer from 'components/chats/ChatExplainer';
import ChatsList from 'components/chats/ChatsList';
import CaregiverAvatarList from 'components/lists/CaregiverAvatarList';
import PageTopBar from 'components/navigation/PageTopBar';
import SkeletonChats from 'components/skeleton/SkeletonChats';
import { Button } from 'components/ui';
import { colors } from 'design';
import {
  compareByLatestMessageAsc,
  useConversationsByType,
  useConversationsLoadingStatus,
  useLoadedConversationsOnce,
  useMyConversationsSplit,
} from 'hooks/useConversations';
import useScrollRestoration from 'hooks/useScrollRestoration';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { ConversationDTO } from 'services/api/models/ConversationDTO';
import { AssignedConversationSegmentTypes, UnassignedConversationSegmentTypes } from 'store/conversations/types';
import { selectCurrentUser } from 'store/users/selectors';

const { Text } = Typography;

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
    backgroundColor: colors.surface.page,
    position: 'relative',
  },
  container: {
    height: '100%',
    overflowY: 'scroll',
    maxWidth: 600,
    width: '100%',
  },
  patientViewContainer: {
    overflowY: 'scroll',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 8px',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    flex: 1,
    '& img': {
      marginTop: 32,
      marginBottom: 32,
    },
  },
  noConvContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 8px',
    alignItems: 'center',
    marginBottom: 16,
  },
  withConvContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 8px',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: 16,
  },
  topicTitle: {
    color: colors.text.tertiary,
    padding: 4,
    margin: 0,
  },
  topicText: {
    padding: 8,
    textAlign: 'center',
  },
  ctaContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: 8,
    marginTop: 16,
  },
  cardColumn: {
    padding: '0',
    display: 'flex',
    justifyContent: 'center',
  },
  bigCardContainer: {
    padding: 8,
  },
  caregiversListContainer: {
    marginTop: 16,
    marginBottom: 16,
    width: '100%',
  },
  cardIcon: {
    minWidth: 80,
    maxWidth: 80,
  },
  cardSvgIcon: {
    height: 80,
    color: colors.brand.accentStrong,
    fontSize: 80 * 0.75,
  },
});

export default function MessagingHome() {
  // Selectors
  const currentUser = useSelector(selectCurrentUser);
  const conversations = useConversationsByType('assigned');
  const unassignedConversations = useConversationsByType('unassigned');
  const myRequestsConversations = useConversationsByType('myRequest');
  const waitingForReassignmentConversations = useConversationsByType('waitingForReassignment');
  const onHoldConversations = useConversationsByType('onHold');
  const closedConversations = useConversationsByType('closed');
  const conversationLoadingStatus = useConversationsLoadingStatus();
  const loadedConversationsOnce = useLoadedConversationsOnce();
  const { needsReply: assignedNeedsReply, replied: assignedReplied } = useMyConversationsSplit(conversations);

  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles();

  // States
  const [unassignedSegmentValue, setUnassignedSegmentValue] =
    useState<UnassignedConversationSegmentTypes>('unassigned');
  const [assignedSegmentValue, setAssignedSegmentValue] = useState<AssignedConversationSegmentTypes>('assigned');
  const scrollableElement = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollableElement);

  const getConversationsListByUnassignedValue = () => {
    switch (unassignedSegmentValue) {
      // Merge "Demandes de groupe" (unassigned) and "Mes demandes" (myRequest) into a single "En attente"
      case 'unassigned':
        return {
          title: t('home.messages.waitingForMe'),
          conversations: [...(unassignedConversations || []), ...(myRequestsConversations || [])].sort(
            compareByLatestMessageAsc,
          ),
        };
      // Rename current "En attente" to "En redirection"
      case 'waitingForReassignment':
        return {
          title: t('home.messages.waitingForReassignment'),
          conversations: waitingForReassignmentConversations || [],
        };
      default:
        return {
          title: t('home.messages.waitingForMe'),
          conversations: [],
        };
    }
  };

  const getConversationsListByAssignedValue = () => {
    switch (assignedSegmentValue) {
      case 'assigned':
        return {
          title: t('chats.subtitle.assigned'),
          conversations: conversations || [],
        };
      case 'onHold':
        return {
          title: t('chats.subtitle.onHold'),
          conversations: onHoldConversations || [],
        };
      case 'closed':
        return {
          title: t('chats.subtitle.closed'),
          conversations: closedConversations || [],
        };

      default:
        return {
          title: t('chats.subtitle.assigned'),
          conversations: [],
        };
    }
  };

  function renderChats<T>(params: {
    title: string;
    conversations: ConversationDTO[];
    options: { label: string; value: string }[];
    onChange: (value: T) => void;
    headerTitle?: string | null;
    content?: React.ReactNode;
  }) {
    return (
      <>
        {params.headerTitle ? (
          <Title level={4} style={{ margin: 13, marginBottom: 0 }}>
            {params.headerTitle}
          </Title>
        ) : null}
        <Segmented
          options={params.options}
          block
          onChange={value => params.onChange(value as T)}
          style={{ margin: 12 }}
        />
        {conversationLoadingStatus === 'LOADING' ? (
          <SkeletonChats numberOfItems={3} />
        ) : params.content !== undefined ? (
          params.content
        ) : (
          <ChatsList conversations={params.conversations} readOnlyConvs={false} />
        )}
      </>
    );
  }

  const renderUnassignedChats = () => {
    if (!currentUser?.is_caregiver) {
      return null;
    }

    const data = getConversationsListByUnassignedValue();

    return renderChats({
      title: data.title,
      headerTitle: t('chats.subtitle.waitingRoom'),
      conversations: data.conversations,
      options: [
        {
          label: (
            <>
              <DownloadOutlined style={{ marginRight: 4 }} />
              {t('home.messages.waitingForMe')} ({unassignedConversations.length + myRequestsConversations.length})
            </>
          ),
          value: 'unassigned',
        },
        {
          label: (
            <>
              <UploadOutlined style={{ marginRight: 4 }} />
              {t('home.messages.waitingForReassignment')} ({waitingForReassignmentConversations.length})
            </>
          ),
          value: 'waitingForReassignment',
        },
      ],
      onChange: value => setUnassignedSegmentValue(value as UnassignedConversationSegmentTypes),
    });
  };

  const renderAssignedChats = () => {
    if (!currentUser?.is_caregiver) {
      return conversationLoadingStatus === 'LOADING' ? (
        <SkeletonChats numberOfItems={3} />
      ) : (
        <ChatsList conversations={conversations} title={null} readOnlyConvs={false} />
      );
    }

    const data = getConversationsListByAssignedValue();

    const assignedContent =
      assignedSegmentValue === 'assigned' ? (
        <div>
          <ChatsList conversations={assignedNeedsReply} readOnlyConvs={false} />
          <Divider style={{ margin: '4px 0' }}>{t('chats.subtitle.replied')}</Divider>
          <ChatsList conversations={assignedReplied} readOnlyConvs={false} />
        </div>
      ) : undefined;

    return renderChats({
      title: data.title,
      conversations: data.conversations,
      options: [
        { label: `${t('chats.subtitle.assigned')}`, value: 'assigned' },
        { label: `${t('chats.subtitle.onHold')} (${onHoldConversations.length})`, value: 'onHold' },
        {
          label: `${t('chats.subtitle.closed')}`,
          value: 'closed',
        },
      ],
      // Add a title above the selector for the assigned/on hold/closed section
      headerTitle: t('chats.subtitle.myConversations'),
      onChange: value => setAssignedSegmentValue(value as AssignedConversationSegmentTypes),
      content: assignedContent,
    });
  };

  const renderPatientWithConversations = () => (
    <div className={styles.patientViewContainer}>
      {renderUnassignedChats()}
      {renderAssignedChats()}

      <div className={styles.withConvContentContainer}>
        <Text className={styles.topicTitle}>{t('chats.dashboardView.topicToCover')}</Text>
      </div>

      <div className={styles.withConvContentContainer}>
        <Title className={styles.topicTitle} level={5}>
          {t('chats.dashboardView.caregiversTeam')}
        </Title>
        <CaregiverAvatarList direction="horizontal" />
      </div>

      <div className={styles.ctaContainer}>
        <Button size="lg" onClick={() => navigate(routes.messagingHowto.route)} icon={<InfoCircleOutlined />}>
          {t('chats.goToExplainer')}
        </Button>
      </div>
    </div>
  );

  const renderPatientNoConversations = () => (
    <div className={styles.patientViewContainer}>
      <ChatExplainer scrollableElement={scrollableElement} />
    </div>
  );

  const renderLoadingPage = () => (
    <div className={styles.loadingContainer}>
      <IconRound fill={colors.brand.accent} height={48} width={48} />
      <p>{t('chats.loadingPage.message')}</p>
      <Spin />
    </div>
  );

  const renderCaregiver = () => (
    <>
      {renderUnassignedChats()}
      {renderAssignedChats()}
    </>
  );

  return (
    <div className={styles.pageContainer}>
      <PageTopBar title={t('chats.title')} />
      <div className={styles.container} ref={scrollableElement} data-testid="messaging-home-scroll">
        {currentUser?.is_caregiver
          ? renderCaregiver()
          : !loadedConversationsOnce
            ? renderLoadingPage()
            : conversations.length !== 0
              ? renderPatientWithConversations()
              : renderPatientNoConversations()}
      </div>
    </div>
  );
}
